import type { SiteLocation } from "~/store/siteSlice";

import { Autocomplete, TextField, Typography } from "@mui/material";

import useStartingSites from "~/hooks/useStartingSites";

interface SelectSiteProps {
	value: SiteLocation | null;
	onChange: (site: SiteLocation | null) => void;
	/**
	 * Selectable sites override. When omitted, the picker fetches the authenticated user's BPF
	 * FINISH starting-node grants itself (the default path that makes the shell usable past login).
	 * Tests and other tree pickers can supply options directly to bypass the query.
	 */
	options?: SiteLocation[];
}

/**
 * Site / starting-node picker used by `RequireSite`. By default it self-populates from the user's
 * BPF starting-node grants so a field user can choose a site and reach the Finish page; callers may
 * still pass `options` to override. The selected site is persisted in the Redux `site` slice by
 * `RequireSite`.
 */
function SelectSite({ value, onChange, options }: SelectSiteProps) {
	const { sites, isLoading, isError } = useStartingSites();
	const usingFetchedOptions = options === undefined;
	const resolvedOptions = options ?? sites;

	const noGrantedSites = usingFetchedOptions && !isLoading && !isError && resolvedOptions.length === 0;

	return (
		<>
			<Autocomplete<SiteLocation>
				options={resolvedOptions}
				value={value}
				onChange={(_event, next) => onChange(next)}
				getOptionLabel={(option) => option.name}
				isOptionEqualToValue={(option, selected) => option.id === selected.id}
				loading={usingFetchedOptions && isLoading}
				renderInput={(params) => <TextField {...params} label="Select a Site" />}
				fullWidth
			/>
			{usingFetchedOptions && isError && (
				<Typography color="error" variant="body2">
					Could not load your sites. Check your connection and try again.
				</Typography>
			)}
			{noGrantedSites && (
				<Typography color="text.secondary" variant="body2">
					You have no assigned sites. Ask an administrator to grant you a starting location.
				</Typography>
			)}
		</>
	);
}

export default SelectSite;
